import React, { useState, useEffect, createContext, useContext } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';

/**
 * 🌍 Subscription Context - Frictionless Global Subscription System
 * 
 * Key Behaviors:
 * - Auto 7-day trial on signup (no plan selection)
 * - Full Pro access during trial
 * - Subtle trial banner (not intrusive)
 * - Paywall only when: trial ≤2 days, trial expired, or Pro feature attempt after trial
 * - Regional pricing auto-derived from user country
 * - Post-trial: App accessible, Pro features locked, data visible
 */

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { country, pricing } = useLocale();
  
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState(null); // 'expired', 'expiring_soon', 'pro_feature'
  
  // 🆕 Emotional Mode State - Suppress paywalls during emotional support
  const [emotionalModeActive, setEmotionalModeActive] = useState(false);

  // Trial status derived from subscription
  const [trialStatus, setTrialStatus] = useState({
    isTrialing: false,
    daysRemaining: 0,
    isExpired: false,
    isExpiringSoon: false, // ≤2 days
    trialEnd: null
  });

  // Subscription status
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isActive: false,
    isPro: false,
    plan: null,
    canAccessProFeatures: false
  });

  // Fetch subscription status
  const fetchSubscription = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/subscription`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        const sub = data.subscription || {};
        setSubscription(sub);
        
        // Calculate trial status
        const now = new Date();
        const trialEnd = sub.trial_end || sub.trial_ends_at;
        
        if (sub.status === 'trial' && trialEnd) {
          const endDate = new Date(trialEnd);
          const msRemaining = endDate - now;
          // Use Math.max to ensure we never show more than 7 days
          // Math.ceil can sometimes show 8 if the user just signed up
          const rawDaysRemaining = msRemaining / (1000 * 60 * 60 * 24);
          const daysRemaining = Math.min(7, Math.max(0, Math.ceil(rawDaysRemaining)));
          const isExpired = rawDaysRemaining <= 0;
          const isExpiringSoon = daysRemaining <= 2 && daysRemaining > 0;

          setTrialStatus({
            isTrialing: !isExpired,
            daysRemaining: Math.max(0, daysRemaining),
            isExpired,
            isExpiringSoon,
            trialEnd: endDate
          });

          setSubscriptionStatus({
            isActive: !isExpired,
            isPro: !isExpired, // Full Pro during trial
            plan: 'trial',
            canAccessProFeatures: !isExpired
          });

          // Auto-show paywall if expired or expiring soon (but not on Day 1-5)
          // 🆕 EMOTIONAL SAFETY: Check emotional mode before showing paywall
          if (isExpired && !emotionalModeActive) {
            setPaywallTrigger('expired');
            setShowPaywall(true);
          }
        } else if (sub.status === 'active' || sub.status === 'trialing') {
          // Active paid subscription
          setTrialStatus({
            isTrialing: false,
            daysRemaining: 0,
            isExpired: false,
            isExpiringSoon: false,
            trialEnd: null
          });

          setSubscriptionStatus({
            isActive: true,
            isPro: true,
            plan: sub.plan_id || 'pro_monthly',
            canAccessProFeatures: true
          });
        } else {
          // Expired/cancelled - no access to Pro
          setTrialStatus({
            isTrialing: false,
            daysRemaining: 0,
            isExpired: true,
            isExpiringSoon: false,
            trialEnd: null
          });

          setSubscriptionStatus({
            isActive: false,
            isPro: false,
            plan: null,
            canAccessProFeatures: false
          });
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchSubscription();
  }, [isAuthenticated, user]);

  // Trigger paywall for Pro feature attempt
  // 🆕 EMOTIONAL SAFETY: Do NOT show paywall during emotional mode
  const requirePro = (featureName = 'this feature') => {
    // Check if emotional mode is active - NEVER show paywall during emotional support
    if (emotionalModeActive) {
      console.log('🔒 Paywall suppressed - emotional mode active');
      return true; // Allow access during emotional mode for safety
    }
    
    if (!subscriptionStatus.canAccessProFeatures) {
      setPaywallTrigger('pro_feature');
      setShowPaywall(true);
      return false;
    }
    return true;
  };

  // Show paywall for expiring trial (called from banner)
  // 🆕 EMOTIONAL SAFETY: Do NOT show paywall during emotional mode
  const showExpiringPaywall = () => {
    if (emotionalModeActive) {
      console.log('🔒 Expiring paywall suppressed - emotional mode active');
      return;
    }
    setPaywallTrigger('expiring_soon');
    setShowPaywall(true);
  };

  // Dismiss paywall
  const dismissPaywall = () => {
    // Only allow dismissing if not expired
    if (!trialStatus.isExpired || emotionalModeActive) {
      setShowPaywall(false);
      setPaywallTrigger(null);
    }
  };
  
  // 🆕 Check emotional mode status
  const checkEmotionalMode = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/home/smart-dashboard`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.dashboard?.emotional_mode) {
          setEmotionalModeActive(true);
          // If paywall is showing during emotional mode, dismiss it
          if (showPaywall) {
            setShowPaywall(false);
            console.log('🔒 Auto-dismissed paywall due to emotional mode');
          }
        } else {
          setEmotionalModeActive(false);
        }
      }
    } catch (error) {
      console.error('Error checking emotional mode:', error);
    }
  };
  
  // Check emotional mode when subscription is fetched
  useEffect(() => {
    if (isAuthenticated) {
      checkEmotionalMode();
    }
  }, [isAuthenticated, user]);

  // Start checkout with regional pricing
  const startCheckout = async (planType = 'monthly') => {
    try {
      const planId = planType === 'annual' ? 'pro_annual' : 'pro_monthly';
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/subscription`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            plan_id: planId,
            origin_url: window.location.origin,
            country_code: country // Use detected country for regional pricing
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Checkout error:', error);
      return false;
    }
  };

  // Manage subscription (cancel, resume)
  const manageSubscription = async (action) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/manage`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ action })
        }
      );

      if (response.ok) {
        await fetchSubscription(); // Refresh status
        return true;
      }
      return false;
    } catch (error) {
      console.error('Manage subscription error:', error);
      return false;
    }
  };

  const value = {
    // Status
    subscription,
    trialStatus,
    subscriptionStatus,
    loading,
    
    // 🆕 Emotional Mode Status
    emotionalModeActive,
    
    // Paywall
    showPaywall,
    paywallTrigger,
    dismissPaywall,
    showExpiringPaywall,
    
    // Actions
    requirePro,
    startCheckout,
    manageSubscription,
    refreshSubscription: fetchSubscription,
    checkEmotionalMode,
    
    // Pricing (from locale)
    pricing,
    country
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
